import { useEffect, useState } from 'react'
import { cdnBaseUrl } from '../../env/environment'
import { logEvent } from '../../utils/logUtility'
import { isProdEnvironment, isStageEnvironment, normalizePhoneNumber } from '../../utils/utils'
import type { ConsoleCaptureMode } from './utils/consoleCapture'

export type DebugToolVisibilityState = {
  enabled: boolean
  /** pending until prod allowlist settles; disabled discards the early console queue */
  captureMode: ConsoleCaptureMode
}

/**
 * Single place for show/hide + console-capture mode:
 * - stage → show, capture enabled
 * - prod → show only when CDN JSON isWhitelisted; capture pending until that settles
 * - else → hide, discard capture queue
 */
export function useDebugToolVisibility(
  phoneNumber: string,
  isUserDetailsLoading: boolean
): DebugToolVisibilityState {
  const isStage = isStageEnvironment()
  const isProd = isProdEnvironment()
  const phone = normalizePhoneNumber(phoneNumber ?? '')

  // Prod: hit CDN allowlist once phone is ready. Otherwise skip fetch.
  const allowlist = useAllowlistJson(isProd && !isUserDetailsLoading && phone ? phone : null)

  if (isStage) {
    return { enabled: true, captureMode: 'enabled' }
  }
  if (isProd) {
    // Keep early console queue until phone + allowlist are known
    if (isUserDetailsLoading || !phone || !allowlist.isSettled) {
      return { enabled: false, captureMode: 'pending' }
    }
    if (allowlist.isWhitelisted) {
      return { enabled: true, captureMode: 'enabled' }
    }
    return { enabled: false, captureMode: 'disabled' }
  }
  return { enabled: false, captureMode: 'disabled' }
}

/** Fetch/cache/parse allowlist JSON. Pass `null` to skip. */
function useAllowlistJson(phone: string | null): { isWhitelisted: boolean; isSettled: boolean } {
  const [allowlistState, setAllowlistState] = useState<{
    phone: string | null
    isWhitelisted: boolean
    isSettled: boolean
  }>({ phone: null, isWhitelisted: false, isSettled: true })

  useEffect(() => {
    if (!phone) {
      setAllowlistState({ phone: null, isWhitelisted: false, isSettled: true })
      return
    }

    let cancelled = false
    setAllowlistState({ phone, isWhitelisted: false, isSettled: false })
    void fetchAllowlist(phone).then((ok) => {
      if (!cancelled) setAllowlistState({ phone, isWhitelisted: ok, isSettled: true })
    })

    return () => {
      cancelled = true
    }
  }, [phone])

  // Phone changed this render but effect has not run yet — stay unsettled so we do not discard early logs
  if (phone !== allowlistState.phone) {
    return { isWhitelisted: false, isSettled: phone == null }
  }
  return { isWhitelisted: allowlistState.isWhitelisted, isSettled: allowlistState.isSettled }
}

const allowlistByPhone = new Map<string, Promise<boolean>>()

function fetchAllowlist(phone: string): Promise<boolean> {
  const existing = allowlistByPhone.get(phone)
  if (existing) return existing

  const url = `${cdnBaseUrl}/json/web/debug-tool/allowlist/${phone}.json`
  logAllowlistRequest(url)
  const request = fetch(url, {
    method: 'GET',
    cache: 'force-cache'
  })
    .then(async (response) => {
      if (!response?.ok) {
        logAllowlistResult(url, { phone, ok: false, status: response?.status ?? null })
        return false
      }
      const body = (await response.json()) as { isWhitelisted?: boolean }
      const jsonWhitelisted = body?.isWhitelisted === true
      logAllowlistResult(url, { phone, ok: true, status: response.status, isWhitelisted: jsonWhitelisted, body })
      return jsonWhitelisted
    })
    .catch((error: unknown) => {
      const name = error instanceof Error ? error.name : 'unknown'
      const message = error instanceof Error ? error.message : String(error)
      // CORS and offline both reject here with no status. A readable HTTP error stays in the branch above.
      const likelyNetworkOrCors = name === 'TypeError' && /failed to fetch|load failed|networkerror/i.test(message)
      logAllowlistResult(url, {
        phone,
        ok: false,
        status: null,
        name,
        message,
        likelyNetworkOrCors
      })
      return false
    })

  allowlistByPhone.set(phone, request)
  return request
}

/** One string so the console line copies cleanly. Also written as a network-tab entry. */
function logAllowlistResult(url: string, details: Record<string, unknown>) {
  console.log(`[debug-tool allowlist json] ${JSON.stringify({ url, ...details })}`)
  const status = details.status ?? 'No Status'
  if (details.ok === true) {
    logEvent('info', {
      message: 'API Response Received',
      url,
      method: 'GET',
      status,
      data: details.body ?? details,
      headers: {}
    })
    return
  }
  logEvent('error', {
    message: 'API Response Error',
    url,
    method: 'GET',
    status,
    error: JSON.stringify(details),
    headers: {}
  })
}

function logAllowlistRequest(url: string) {
  logEvent('info', {
    message: 'API Request Initiated',
    url,
    method: 'GET',
    headers: {}
  })
}
